import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { GetNameDialogComponent } from '../get-name.dialog';
import { MatDialog } from '@angular/material/dialog';
import { ConfiguredToastrService } from '../../../helper/toastr/configured-toastr.service';
import { take, takeUntil } from 'rxjs/operators';
import { PlotlyComponent } from '../../../helper/plotly/plotly.component';
import { CommunicationService } from '../../../communication/communication.service';
import { ProjectCommunicationService } from '../../../communication/project-comms.service';
import { GraphSelectorService } from './graph-selector.service';
import { GraphSelectorPackage } from '../../../models/data-structures/results/graph-selector-package.model';
import { AnessaTranslationService } from '../../../helper/translation/anessa-translation.service';
import { AnessaTranslationInterface } from '../../../models/interfaces/anessa-translation.interface';
import { UserpilotReloader } from '../../../helper/userpilot-reloader.component';

@Component({
  selector: 'app-graph-selector',
  templateUrl: './graph-selector.component.html',
  styleUrls: ['./graph-selector.component.scss', '../universal-styling.scss']
})
export class GraphSelectorComponent extends UserpilotReloader implements OnInit, OnChanges, AfterViewInit, OnDestroy, AnessaTranslationInterface {
  @Input() xAxisLabel: string;
  @Input() yAxisLabel = '';
  @Input() idPrefix = '';
  @Input() customLayout;
  @Input() graphData: any;
  @Input() graphType = 'bar';
  @Input() graphLabel: any;
  @Input() currencyLabel: string;
  @Input() hideSaveToReport = false;
  @Input() hideSaveButtons = false;
  @Input() precision = -1;
  @Input() view: any[] = [1000, 600];
  @Input() plotlyLegend: any = {'orientation': 'h', x: -0.05, y: -0.3};
  @ViewChild('widthElement', {static: true}) widthElement: ElementRef;
  @ViewChild('chartElement', {static: true}) chartElement: ElementRef;
  @ViewChild('graphCanvas') canvasElement: ElementRef;
  @ViewChild('plotlyComponent') plotlyComponent: PlotlyComponent;
  onDestroyNotifier = new EventEmitter();
  counter = 0;
  // options
  plotFramework = 'plotly';
  showXAxis = true;
  showYAxis = true;
  gradient = false;
  showYAxisLabel = true;
  showXAxisLabel = true;
  imageName = '';
  showChart = false;

  // Chart size is auto reduced to 800 width when legend is used. This causes problems when drawing to canvas, so the
  // assumed 'constant' of 160 is here as well.
  chartSize = [0, 0];
  legendWidth = 0;

  colorScheme = {
    domain: ['#262262']
  };
  trigger: string;


  constructor(
    private commService: CommunicationService,
    private projCommService: ProjectCommunicationService,
    public dialog: MatDialog,
    public toastrService: ConfiguredToastrService,
    private graphSelectorService: GraphSelectorService,
    private translationService: AnessaTranslationService
  ) {
    super();
  }

  ngOnInit() {
    if (this.graphType === 'bargroup') {
      this.colorScheme = {
        domain: ['#262262', '#ef4136', '#39b54a']
      };
    }
    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier))
      .subscribe(language => {
        this.trigger = language;
      });
  }

  ngOnChanges() {
    if (this.graphType != null && this.graphData != null) {
      this.processData();
    }
  }

  ngAfterViewInit() {
    if (this.graphType !== null && this.graphData !== null) {
      this.processData();
    }
  }

  ngOnDestroy() {
    this.onDestroyNotifier.emit();
    this.onDestroyNotifier.unsubscribe();
  }

  restoreLegend() {
    this.plotlyComponent.thePlot.data.forEach(trace => {
      if (trace.visible === 'legendonly') {
        delete trace['showlegend'];
      }
    });
  }

  /**
   * Remove any grayed out items in the graph legend before exporting graph image
   * Unused legends are restored once image has been saved
   */
  private hideUnusedLegends() {
    const tracesFiltered = this.plotlyComponent.thePlot.data.some(trace => trace.visible === true || trace.visible === 'legendonly');
    if (tracesFiltered) {
      // Hide legend for hidden traces
      this.plotlyComponent.thePlot.data.forEach(trace => {
        if (trace.visible === 'legendonly') {
          trace.showlegend = false;
        }
      });
      // Restore legends after graph image is saved
      this.graphSelectorService.graphSavedNotifier
        .pipe(take(1))
        .subscribe(() => {
          this.restoreLegend();
        });
    }
  }

  openDialog(isSaveToReport: boolean): void {
    const dialogRef = this.dialog.open(GetNameDialogComponent, {
      width: 'max-content',
      data: {name: this.imageName, instruction: this.translationService.get('RESULTS.GENERAL.INPUT_GRAPH_NAME')}
    });

    dialogRef.afterClosed().pipe(take(1)).subscribe(result => {
      if (result && result.saveClicked) {
        if (result.name !== undefined) {
          this.imageName = result.name;
          this.hideUnusedLegends();
          if (isSaveToReport) {
            this.saveImageData();
          } else {
            switch (this.plotFramework) {
              case 'ngx':
                this.saveLocal();
                break;
              case 'plotly':
                this.downloadPlotlyAsImage();
                break;
            }
          }
        }
      }
    });
  }

  xAxisTickFormatting(val) {
    this.counter++;
    if (this.graphData.length <= 25) {
      return val;
    } else {
      if (this.counter % 3 === 0) {
        return val;
      } else {
        return '';
      }
    }
  }

  saveImageData() {
    this.saveGraphData();
  }

  saveGraphData() {
    if (this.graphData == null) {
      return;
    }
    const pack = new GraphSelectorPackage();
    const graphDat = {
      graphType: this.graphType,
      xAxisLabel: this.xAxisLabel,
      yAxisLabel: this.yAxisLabel,
      graphData: this.graphData
    };
    pack.request = {
      projectId: this.projCommService.getProjectId(),
      name: this.imageName,
      graphData: graphDat,
      graphType: this.graphType
    };
    pack.imageName = this.imageName;
    pack.plotFramework = this.plotFramework;
    pack.plotlyComponent = this.plotlyComponent;
    pack.chartElement = this.chartElement;
    pack.chartSize = this.chartSize;
    pack.legendWidth = this.legendWidth;
    pack.canvasElement = this.canvasElement;
    this.graphSelectorService.saveGraphData(pack);
  }

  saveLocal() {
    const pack = new GraphSelectorPackage();
    pack.chartSize = this.chartSize;
    pack.legendWidth = this.legendWidth;
    pack.chartElement = this.chartElement;
    pack.imageName = this.imageName;
    const svgElement = this.graphSelectorService.getSVGElement(pack);
    const contextBind = this;
    if (svgElement != null && svgElement instanceof SVGElement) {
      this.graphSelectorService.prepareForCanvas(svgElement, pack);
      let svgURL = new XMLSerializer().serializeToString(svgElement);
      svgURL = svgURL.replace(new RegExp('<rect[^\>]*\>', 'g'), '');
      const img = new Image();
      const ctx = this.canvasElement.nativeElement.getContext('2d');
      const canvasBind: HTMLCanvasElement = this.canvasElement.nativeElement;
      canvasBind.width = this.chartSize[0] * 2;
      canvasBind.height = (this.chartSize[1]) * 2;
      img.onload = function () {
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvasBind.width, canvasBind.height);
        ctx.drawImage(img, 0, 0, canvasBind.width, canvasBind.height);

        canvasBind.toDataURL('image/png');
        const event = new MouseEvent('click', {
          'view': window,
          'bubbles': true,
          'cancelable': true
        });
        canvasBind.toBlob(function (blob) {
          const newImg = document.createElement('img'),
            url = URL.createObjectURL(blob);
          newImg.onload = function () {
            URL.revokeObjectURL(url);
          };
          newImg.src = url;
          contextBind.graphSelectorService.teardownAfterCanvas(svgElement, pack);
          const a = document.createElement('a');
          a.setAttribute('download', contextBind.imageName);
          a.setAttribute('href', url);
          a.setAttribute('target', '_blank');
          a.dispatchEvent(event);
        }, 'image/jpeg', 0.8);

      };
      img.src = 'data:image/svg+xml; charset=utf8, ' + encodeURIComponent(svgURL);
    }
  }

  processData() {
    if (this.currencyLabel) {
      this.yAxisLabel = this.translationService.get('RESULTS.CASH_FLOWS.CURR_IN') + ' ' + this.currencyLabel;
    }
    if (this.widthElement != null) {
      // sets the size of the graph to the width of the containing element
      this.chartSize[0] = this.chartElement.nativeElement.clientWidth;
      this.chartSize[1] = this.chartElement.nativeElement.clientHeight;

    }
    this.showChart = true;
  }

  // tslint:disable-next-line:member-ordering
  boundTickFormatting = this.xAxisTickFormatting.bind(this);

  downloadPlotlyAsImage() {
    const supportedFormats: string[] = ['jpeg', 'png', 'svg'];
    const re = /(?:\.([^.]+))?$/;
    const fileExtension = re.exec(this.imageName)?.[1];
    let format: allowedFormats = 'png';
    let filename = this.imageName;
    if (supportedFormats.includes(fileExtension)) {
      format = fileExtension as allowedFormats;
      filename = filename.replace(new RegExp('\.' + format), '');
    }
    window['Plotly'].downloadImage(
      this.plotlyComponent.thePlot.plotEl.nativeElement,
      {format: format, filename: filename, width: 1000, height: 600}
    );
    this.restoreLegend();
  }

  promiseSaveImageAs(fileName?: string, purpose?: string): Promise<string> {
    if (!fileName) {
      fileName = Date.now().toString() + '.png';
    }
    const nameAndFormat = fileName.split('.');
    const nameOnly = nameAndFormat[0];
    const format = nameAndFormat[1];
    const supportedFormats: string[] = ['jpeg', 'png', 'svg'];
    if (!supportedFormats.includes(format)) {
      throw new Error('invalid file format');
    }
    const options: Plotly.ToImgopts = {format: format as allowedFormats, width: 1000, height: 600};
    return new Promise((fulfill, reject) => {
      window['Plotly'].toImage(this.plotlyComponent.thePlot.plotEl.nativeElement, options)
        .then(dataUri => {
          let blob;
          if (options.format === 'svg') {
            blob = new window.Blob([dataUri], {type: 'image/svg+xml;charset=utf-8'});
          } else {
            const encodedString = dataUri.split(',')[1];
            const binary = this.graphSelectorService.fixBinary(window.atob(encodedString));
            blob = new window.Blob([binary], {type: 'image/' + options.format});
          }
          const uploadDoneNotifier = new EventEmitter();
          this.commService.storeBlobAsFileSilently(blob, nameOnly, purpose)
            .pipe(takeUntil(this.onDestroyNotifier))
            .pipe(takeUntil(uploadDoneNotifier))
            .subscribe(
              absolutePath => {
                if (absolutePath && typeof absolutePath === 'string') {
                  uploadDoneNotifier.emit();
                  fulfill(absolutePath);
                }
              }
            );
          }
        );
    });
  }

  getPrefixedId(id: string) {
    return this.idPrefix !== "" ? this.idPrefix + "_" + id : id
  }
}

type allowedFormats = "jpeg" | "png" | "svg";